import moment from 'moment';
import { columnChunkId } from './chunk.js';
import { tilesChunks } from './tilesChunks';

const unscheduledPlanDate = '0000-00-00';
const suggestedTilesStatus = 'user_submitted';

const getPreviousTileId = (
  dndContextSection,
  newTilesIds,
  columnIndex,
  destination,
) => {
  const previousColumnId = dndContextSection.columnOrder[columnIndex - 1];
  const previousColumn = previousColumnId
    ? dndContextSection.columns[previousColumnId]
    : null;
  const previousColumnTilesIds = previousColumn ? previousColumn.tilesIds : [];
  const previousTileFromPreviousColumn = previousColumn
    ? previousColumnTilesIds[previousColumnTilesIds.length - 1]
    : null;
  const previousTileId =
    destination.index === 0
      ? previousTileFromPreviousColumn
      : newTilesIds[destination.index - 1];
  const unscheduledTab = destination.droppableId === unscheduledPlanDate;

  if (
    destination.droppableId.includes(unscheduledPlanDate) &&
    !previousTileId &&
    !unscheduledTab
  ) {
    const currentColumn = dndContextSection.columns[destination.droppableId];
    const groupedColumns =
      dndContextSection.groupedColumns[currentColumn.title];
    const columnsWithTiles = groupedColumns
      .map(colId => {
        const col = dndContextSection.columns[colId];
        return col.tilesIds.length > 0 ? col : null;
      })
      .filter(col => col);
    const lastColumnWithTiles = columnsWithTiles[columnsWithTiles.length - 1];
    const tilesIds = lastColumnWithTiles ? lastColumnWithTiles.tilesIds : null;
    const lastTile = tilesIds ? tilesIds[tilesIds.length - 1] : null;

    return lastTile;
  }

  return previousTileId;
};

const getNextTileId = (
  dndContextSection,
  newTilesIds,
  columnIndex,
  destination,
) => {
  const lastIndex = dndContextSection.tilesPerRow - 1;
  const nextColumnId = dndContextSection.columnOrder[columnIndex + 1];
  const nextColumn = nextColumnId
    ? dndContextSection.columns[nextColumnId]
    : null;
  const nextColumnTilesIds = nextColumn ? nextColumn.tilesIds : [];
  const nextTileId =
    destination.index === lastIndex
      ? nextColumn
        ? nextColumnTilesIds[0]
        : null
      : newTilesIds[destination.index + 1];

  return nextTileId;
};

const reOrderTiles = (column, dndContextSection) => {
  const columnGroup = dndContextSection.groupedColumns[column.title];
  const columnsToReOrder = columnGroup;
  const tilesIds = columnsToReOrder.reduce((acc, colId) => {
    const column = dndContextSection.columns[colId];

    return acc.concat(column.tilesIds);
  }, []);

  const tilesIdsChunks = tilesChunks(
    column.title,
    tilesIds,
    dndContextSection.tilesPerRow,
  );

  const newColumns = tilesIdsChunks.reduce((acc, currentChunk, index) => {
    const columnId = columnsToReOrder[index];
    const column = dndContextSection.columns[columnId];

    if (column) {
      return {
        ...acc,
        [column.id]: {
          ...column,
          tilesIds: currentChunk,
        },
      };
    }
    const previousColumnId = columnsToReOrder[index - 1];
    const previousColumn = dndContextSection.columns[previousColumnId];
    const newColumnId = columnChunkId(previousColumn.title, index);
    const newColumn = {
      id: newColumnId,
      title: previousColumn.title,
      tilesIds: currentChunk,
    };

    return {
      ...acc,
      [newColumnId]: newColumn,
    };
  }, {});

  if (tilesIdsChunks.length < columnsToReOrder.length) {
    columnsToReOrder.map((colId, index) => {
      if (index >= tilesIdsChunks.length) {
        const column = dndContextSection.columns[colId];

        newColumns[column.id] = { ...column, tilesIds: [] };
      }
    });
  }
  return newColumns;
};

const DragAndDropToSameColumn = (
  dndContextSection,
  originalColumn,
  newColumn, // don't remove without refactor with DragAndDropToDifferentColumn
  source,
  destination,
  draggableId,
) => {
  const newTilesIds = [...originalColumn.tilesIds];

  newTilesIds.splice(source.index, 1);
  newTilesIds.splice(destination.index, 0, draggableId);
  const originalColumnIndex = dndContextSection.columnOrder.indexOf(
    originalColumn.id,
  );
  const previousTileId = getPreviousTileId(
    dndContextSection,
    newTilesIds,
    originalColumnIndex,
    destination,
  );
  const nextTileId = getNextTileId(
    dndContextSection,
    newTilesIds,
    originalColumnIndex,
    destination,
  );

  const newOriginalColumn = {
    ...originalColumn,
    tilesIds: newTilesIds,
  };

  const newColumns = {
    ...dndContextSection.columns,
    [newOriginalColumn.id]: newOriginalColumn,
  };

  const newDndContextSection = {
    ...dndContextSection,
    columns: newColumns,
  };

  return {
    newDndContextSection,
    previousTileId,
    nextTileId,
  };
};

const DragAndDropToDifferentColumns = (
  dndContextSection,
  originalColumn,
  newColumn,
  source,
  destination,
  draggableId,
) => {
  const originalColumnTilesIds = Array.from(originalColumn.tilesIds);

  originalColumnTilesIds.splice(source.index, 1);

  const newOriginalColumn = {
    ...originalColumn,
    tilesIds: originalColumnTilesIds,
  };

  const newColumnTilesIds = Array.from(newColumn.tilesIds);

  newColumnTilesIds.splice(destination.index, 0, draggableId);

  const newNewColumn = {
    ...newColumn,
    tilesIds: newColumnTilesIds,
  };

  const newColumnIndex = dndContextSection.columnOrder.indexOf(newColumn.id);

  const newDndContextSection = {
    ...dndContextSection,
    columns: {
      ...dndContextSection.columns,
      [originalColumn.id]: newOriginalColumn,
      [newColumn.id]: newNewColumn,
    },
  };

  newDndContextSection.columns = {
    ...newDndContextSection.columns,
    ...reOrderTiles(newNewColumn, newDndContextSection),
    ...reOrderTiles(newOriginalColumn, newDndContextSection),
  };

  const columnWithTiles = Object.entries(newDndContextSection.columns)
    .filter(col => {
      return (
        col[1].title === unscheduledPlanDate || col[1].tilesIds.length >= 0
      );
    })
    .reduce((acc, col) => {
      return {
        ...acc,
        [col[0]]: col[1],
      };
    }, {});

  const newColumnGroups = Object.entries(columnWithTiles).reduce((acc, col) => {
    const attributes = col[1];
    acc[attributes.title] = [...(acc[attributes.title] || []), attributes.id];
    return acc;
  }, {});

  const newColumnOrder = Object.entries(columnWithTiles).map(col => col[0]);

  newDndContextSection.columns = columnWithTiles;
  newDndContextSection.groupedColumns = newColumnGroups;
  newDndContextSection.columnOrder = newColumnOrder;

  const previousTileId = getPreviousTileId(
    dndContextSection,
    newColumnTilesIds,
    newColumnIndex,
    destination,
  );
  const nextTileId = getNextTileId(
    dndContextSection,
    newColumnTilesIds,
    newColumnIndex,
    destination,
  );

  return {
    newDndContextSection,
    previousTileId,
    nextTileId,
  };
};

const findTileByIdAndPlanDate = (tiles, id, planDate) => {
  return tiles.find(tile => tile.id === id && tile.planDate === planDate);
};

const BuildSortParams = (
  planOrUnscheduledTab,
  movingFromGenericColumn,
  newPlanDate,
  tiles,
  previousTileId,
  nextTileId,
  draggableId,
) => {
  let leftTileId = previousTileId;
  let rightTileId = nextTileId;

  if (planOrUnscheduledTab && movingFromGenericColumn) {
    const currentPlanDateTiles = tiles.filter(t => t.planDate === newPlanDate);
    const leftId = Number(previousTileId, 10);
    const rightId = Number(nextTileId, 10);
    const leftTile = findTileByIdAndPlanDate(
      currentPlanDateTiles,
      leftId,
      newPlanDate,
    );
    const rightTile = findTileByIdAndPlanDate(
      currentPlanDateTiles,
      rightId,
      newPlanDate,
    );

    leftTileId = leftTile ? previousTileId : null;
    rightTileId = rightTile ? nextTileId : null;
  }

  return {
    tileId: draggableId,
    leftTileId,
    rightTileId,
    planDate: newPlanDate,
  };
};

const dragFromSuggestedToSomewhere = (source, destination) => {
  // can't drag from suggested column to outside of it
  return (
    source.droppableId.match(suggestedTilesStatus) &&
    !destination.droppableId.match(suggestedTilesStatus)
  );
};

const dragFromSomewhereToSuggested = (source, destination) => {
  // can't drag from outside to the suggested column
  return (
    !source.droppableId.match(suggestedTilesStatus) &&
    destination.droppableId.match(suggestedTilesStatus)
  );
};

const droppedOnSamePosition = (source, destination) => {
  return (
    destination.droppableId === source.droppableId &&
    destination.index === source.index
  );
};

const ProcessDragEnd = (source, destination) => {
  if (
    !destination ||
    droppedOnSamePosition(source, destination) ||
    dragFromSuggestedToSomewhere(source, destination) ||
    dragFromSomewhereToSuggested(source, destination)
  ) {
    return false;
  }

  return true;
};

const getUpdatedTiles = (column, dndContextSection) => {
  const columnGroup = dndContextSection.groupedColumns[column.title];
  const columnTilesIds = columnGroup.reduce((acc, c) => {
    const col = dndContextSection.columns[c];

    if (col.tilesIds.length > 0) {
      return acc.concat(col.tilesIds);
    }
    return acc;
  }, []);

  return Object.values(dndContextSection.tiles).filter(tile =>
    columnTilesIds.includes(tile.id.toString()),
  );
};

const UpdateTiles = (column, dndContextSection, updateBucketTiles) => {
  if (moment(column.title).isValid()) {
    const updatedTiles = getUpdatedTiles(column, dndContextSection);

    updateBucketTiles({
      digestDate: column.title,
      tiles: updatedTiles,
    });
  }
};

const DropTileToEmptyDigest = (
  dndContext,
  source,
  destination,
  draggableId,
  setDragAndDropContext,
) => {
  const draggedToNewDigest = { ...source, tileId: draggableId };

  // hide tile, until user finish flow of creating digest or cancel it
  document.querySelector(`#single-tile-${draggableId}`).style.display = 'none';

  // hack to open create digest modal for correct month
  document
    .querySelector(`[data-rfd-droppable-id="${destination.droppableId}"]`)
    .getElementsByTagName('span')[0]
    .click();

  setDragAndDropContext({
    ...dndContext,
    draggedToNewDigest: draggedToNewDigest,
  });
};

export {
  DragAndDropToSameColumn,
  DragAndDropToDifferentColumns,
  BuildSortParams,
  ProcessDragEnd,
  UpdateTiles,
  DropTileToEmptyDigest,
};
